import type {
  ChatDebitResponse,
  HeartbeatResponse,
  SessionCheckResponse,
  SessionMode,
  SessionTokenResponse,
  WaitlistResponse,
} from '@jas/shared';

const BASE = process.env.NEXT_PUBLIC_API_BASE_URL ?? 'http://localhost:3001';

export class BudgetExhaustedError extends Error {
  constructor(public readonly remainingSeconds: number, public readonly budgetSeconds: number) {
    super('Daily budget exhausted');
  }
}

export async function fetchSessionCheck(fingerprint: string): Promise<SessionCheckResponse> {
  const res = await fetch(`${BASE}/api/session/check?fp=${encodeURIComponent(fingerprint)}`);
  if (!res.ok) throw new Error(`session/check failed: ${res.status}`);
  return res.json() as Promise<SessionCheckResponse>;
}

export async function fetchSessionToken(
  fingerprint: string,
  turnstileToken?: string,
  mode?: SessionMode,
): Promise<SessionTokenResponse> {
  const res = await fetch(`${BASE}/api/session/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ fingerprint, turnstileToken, mode }),
  });
  if (res.status === 429) {
    const body = (await res.json().catch(() => ({}))) as {
      remainingSeconds?: number;
      budgetSeconds?: number;
    };
    throw new BudgetExhaustedError(body.remainingSeconds ?? 0, body.budgetSeconds ?? 300);
  }
  if (!res.ok) throw new Error(`session/token failed: ${res.status}`);
  return res.json() as Promise<SessionTokenResponse>;
}

export async function postHeartbeat(sessionToken: string): Promise<HeartbeatResponse> {
  const res = await fetch(`${BASE}/api/session/heartbeat`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  if (!res.ok) throw new Error(`session/heartbeat failed: ${res.status}`);
  return res.json() as Promise<HeartbeatResponse>;
}

/**
 * Chat-mode equivalent of postHeartbeat — called once per sent message.
 * The backend debits a fixed CHAT_TURN_DEBIT_SECONDS from the shared
 * rate-limit bucket and returns what's left so the UI can show
 * remaining messages and trigger the waitlist when exhausted.
 */
export async function postChatDebit(sessionToken: string): Promise<ChatDebitResponse> {
  const res = await fetch(`${BASE}/api/session/chat-debit`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${sessionToken}` },
  });
  if (!res.ok) throw new Error(`session/chat-debit failed: ${res.status}`);
  return res.json() as Promise<ChatDebitResponse>;
}

export async function postWaitlist(email: string): Promise<WaitlistResponse> {
  const res = await fetch(`${BASE}/api/waitlist`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error(`waitlist failed: ${res.status}`);
  return res.json() as Promise<WaitlistResponse>;
}

/**
 * Fire-and-forget heartbeat for the live-visitor count on the admin
 * dashboard. Failures are swallowed — this is a best-effort signal,
 * not a load-bearing call. Returns true on success for the rare caller
 * (tests) that wants to observe it.
 */
export async function pingPresence(fingerprint: string): Promise<boolean> {
  try {
    const res = await fetch(`${BASE}/api/presence/ping`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fingerprint }),
      // Use `keepalive` so a ping issued just before unload still
      // gets a chance to flush — the count is only "live" if last-ping
      // signals don't get lost on tab close.
      keepalive: true,
    });
    return res.ok;
  } catch {
    return false;
  }
}
